import { useCallback, useEffect, useState, Fragment } from "react";
import type { UserProfile } from "../lib/types";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { Banner, CopyId, SearchBar } from "../components/ui";

// The admin_users_list edge fn caps its page at 100 and (unlike the corrections
// list) supports neither offset nor a `total` count, so true offset paging isn't
// possible without a backend change. We fetch the server max and surface a
// truncation notice so past-100 records aren't silently invisible (ADM-7).
const USERS_PAGE = 100;

type UserListItem = {
  id: string;
  email?: string;
  created_at?: string;
  last_sign_in_at?: string;
};

export default function UsersPage() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { limit: USERS_PAGE };
      if (q.trim()) body.q = q.trim();
      const data = await invokeAdmin<{ users: UserListItem[] }>("admin_users_list", body);
      setUsers(data.users);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function expandUser(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      setProfile(null);
      return;
    }
    setExpandedId(id);
    setProfile(null);
    setProfileLoading(true);
    try {
      const data = await invokeAdmin<UserProfile>("admin_user_get", { user_id: id });
      setProfile(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setProfileLoading(false);
    }
  }

  async function deleteUser(id: string, email?: string) {
    if (!window.confirm(`Delete user ${email ?? id}? This cannot be undone.`)) return;
    setDeleteLoading(id);
    setError(null);
    try {
      await invokeAdmin("admin_user_delete", { user_id: id });
      setUsers((prev) => prev.filter((u) => u.id !== id));
      if (expandedId === id) {
        setExpandedId(null);
        setProfile(null);
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setDeleteLoading(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Users</h1>

      <div className="mt-4">
        <SearchBar
          value={q}
          onChange={setQ}
          onSearch={fetchUsers}
          placeholder="Search by email…"
          loading={loading}
        />
      </div>

      <div className="mt-2 text-xs text-zinc-500">
        Showing {users.length} user{users.length === 1 ? "" : "s"}
        {users.length === USERS_PAGE && (
          <span className="text-amber-500"> — capped at {USERS_PAGE}; refine your search to see more.</span>
        )}
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium">Last sign-in</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <Fragment key={u.id}>
                <tr
                  className={`border-t border-zinc-800 ${expandedId === u.id ? "bg-zinc-800/40" : ""}`}
                >
                  <td className="px-3 py-2 text-zinc-300">{u.email ?? "—"}</td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">
                    {u.last_sign_in_at
                      ? new Date(u.last_sign_in_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => expandUser(u.id)}
                        className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600"
                      >
                        {expandedId === u.id ? "Close" : "View"}
                      </button>
                      <button
                        onClick={() => deleteUser(u.id, u.email)}
                        disabled={deleteLoading === u.id}
                        className="rounded bg-red-800 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deleteLoading === u.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === u.id && (
                  <tr key={`${u.id}-detail`} className="border-t border-zinc-800">
                    <td colSpan={4} className="px-4 py-4 bg-zinc-900">
                      {profileLoading ? (
                        <p className="text-sm text-zinc-500">Loading…</p>
                      ) : profile ? (
                        <div className="space-y-3 text-sm">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-xs text-zinc-500 uppercase">ID</span>
                              <div className="mt-0.5">
                                <CopyId id={profile.id} chars={36} />
                              </div>
                            </div>
                            <div>
                              <span className="text-xs text-zinc-500 uppercase">Email</span>
                              <p className="text-zinc-300 text-xs mt-0.5">{profile.email ?? "—"}</p>
                            </div>
                            <div>
                              <span className="text-xs text-zinc-500 uppercase">Event saves</span>
                              <p className="text-zinc-300 text-xs mt-0.5">{profile.event_saves_count}</p>
                            </div>
                            <div>
                              <span className="text-xs text-zinc-500 uppercase">Place follows</span>
                              <p className="text-zinc-300 text-xs mt-0.5">{profile.place_follows_count}</p>
                            </div>
                          </div>
                          {profile.profile && (
                            <>
                              <div>
                                <span className="text-xs text-zinc-500 uppercase">Profile data</span>
                                <pre className="mt-1 rounded bg-zinc-800 p-2 text-xs text-zinc-300 overflow-x-auto max-h-40 overflow-y-auto">
                                  {JSON.stringify(profile.profile.data, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <span className="text-xs text-zinc-500 uppercase">Taxonomy weights</span>
                                <pre className="mt-1 rounded bg-zinc-800 p-2 text-xs text-zinc-300 overflow-x-auto max-h-40 overflow-y-auto">
                                  {JSON.stringify(profile.profile.taxonomy_weights, null, 2)}
                                </pre>
                              </div>
                            </>
                          )}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {users.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
